import { z } from "zod";

export const MAX_PROMPT_LENGTH = 500;
export const MAX_TITLE_LENGTH = 200;
export const MAX_DESCRIPTION_LENGTH = 1000;
export const MAX_ASSESSMENTS = 50;
export const MAX_QUESTIONS_PER_ASSESSMENT = 50;
export const MAX_CHOICE_SETS = 30;
export const MIN_CHOICES_PER_SET = 2;
export const MAX_CHOICES_PER_SET = 10;
export const MAX_SCALE_VALUE = 100;
export const MIN_SCALE_VALUE = 0;
export const MAX_SCALE_STEPS = 21;

const promptSchema = z.string().max(MAX_PROMPT_LENGTH);
const titleSchema = z.string().max(MAX_TITLE_LENGTH);
const descriptionSchema = z.string().max(MAX_DESCRIPTION_LENGTH);

const scaleQuestionSchema = z.object({
    id: z.string().min(1),
    type: z.literal("scale"),
    prompt: promptSchema,
    min: z.number().int(),
    max: z.number().int(),
    min_label: promptSchema.optional(),
    max_label: promptSchema.optional()
});

const textQuestionSchema = z.object({
    id: z.string().min(1),
    type: z.literal("text"),
    prompt: promptSchema
});

const multipleChoiceQuestionSchema = z.object({
    id: z.string().min(1),
    type: z.literal("multiple_choice"),
    prompt: promptSchema,
    choice_set_id: z.string().min(1)
});

const questionSchema = z.discriminatedUnion("type", [
    scaleQuestionSchema,
    textQuestionSchema,
    multipleChoiceQuestionSchema
]);

const choiceSchema = z.object({
    id: z.string().min(1),
    label: promptSchema
});

const choiceSetSchema = z.object({
    id: z.string().min(1),
    title: titleSchema.optional(),
    choices: z.array(choiceSchema).min(MIN_CHOICES_PER_SET).max(MAX_CHOICES_PER_SET)
});

const assessmentSchema = z.object({
    id: z.string().min(1),
    title: titleSchema,
    description: descriptionSchema.optional(),
    questions: z.array(questionSchema).max(MAX_QUESTIONS_PER_ASSESSMENT)
});

export const assessmentsDocumentSchema = z.object({
    schema_version: z.number().int(),
    choice_sets: z.array(choiceSetSchema).max(MAX_CHOICE_SETS),
    assessments: z.array(assessmentSchema).max(MAX_ASSESSMENTS),
    updated_at: z.number().int().optional(),
    updated_by: z.string().optional()
});

export function clampPrompt(value: string): string {
    return value.slice(0, MAX_PROMPT_LENGTH);
}

export function clampTitle(value: string): string {
    return value.slice(0, MAX_TITLE_LENGTH);
}

export function clampDescription(value: string): string {
    return value.slice(0, MAX_DESCRIPTION_LENGTH);
}

export function validateAssessmentsDocument(document: unknown): string | null {
    const parsed = assessmentsDocumentSchema.safeParse(document);
    if (!parsed.success) {
        return parsed.error.issues[0]?.message ?? "Invalid assessments document";
    }

    const seen = new Set<string>();
    const choiceSetIds = new Set(parsed.data.choice_sets.map(cs => cs.id));

    for (const choiceSet of parsed.data.choice_sets) {
        for (const id of [choiceSet.id, ...choiceSet.choices.map(c => c.id)]) {
            if (seen.has(id)) {
                return `Duplicate id: ${id}`;
            }
            seen.add(id);
        }
        for (const choice of choiceSet.choices) {
            if (!choice.label.trim()) {
                return `Choice ${choice.id} label cannot be empty`;
            }
        }
    }

    for (const assessment of parsed.data.assessments) {
        if (seen.has(assessment.id)) {
            return `Duplicate id: ${assessment.id}`;
        }
        seen.add(assessment.id);

        if (!assessment.title.trim()) {
            return `Assessment ${assessment.id} title cannot be empty`;
        }

        for (const question of assessment.questions) {
            if (seen.has(question.id)) {
                return `Duplicate id: ${question.id}`;
            }
            seen.add(question.id);

            if (!question.prompt.trim()) {
                return `Question ${question.id} prompt cannot be empty`;
            }

            if (question.type === "scale") {
                if (question.min >= question.max) {
                    return `Question ${question.id}: min must be less than max`;
                }
                if (question.min < MIN_SCALE_VALUE || question.max > MAX_SCALE_VALUE) {
                    return `Question ${question.id}: scale must be within ${MIN_SCALE_VALUE}–${MAX_SCALE_VALUE}`;
                }
                if (question.max - question.min + 1 > MAX_SCALE_STEPS) {
                    return `Question ${question.id}: scale has too many steps (max ${MAX_SCALE_STEPS})`;
                }
            }

            if (question.type === "multiple_choice" && !choiceSetIds.has(question.choice_set_id)) {
                return `Question ${question.id} references unknown choice_set_id: ${question.choice_set_id}`;
            }
        }
    }

    return null;
}
